import { KeyframeMetadata } from '../../internal/playbackAuthorizationService/internal/PlaybackAuthorizationResponse';

const DEFAULT_FREQUENCY = 16;
const DEFAULT_COLUMNS = 100;

/**
 * Derives sprite keyframe metadata from a sprite sheet URL.
 *
 * The function uses `split()` and `pop()` to extract the filename from
 * the URL, `lastIndexOf()` and `substring()` to remove the file extension,
 * and `split()` to separate the filename components. The last three
 * components are parsed as width, height, and tile count using `at()`.
 *
 * The sprite sheet is assumed to use 100 columns, and the number of rows
 * is calculated from the tile count using `Math.ceil()`.
 *
 * For example, a URL ending with:
 *   ..._IND_192_108_5.jpg
 *
 * is interpreted as:
 *   width     = 192
 *   height    = 108
 *   tileCount = 5
 *
 * The function returns the derived KeyframeMetadata when the extracted
 * values are valid positive numbers. Otherwise, it returns undefined.
 */

export function deriveSpriteKeyframe(spriteUrl: string): KeyframeMetadata | undefined {
  const fileName = spriteUrl.split('/').pop();

  if (!fileName) {
    return undefined;
  }

  const baseName = fileName.substring(0, fileName.lastIndexOf('.'));

  if (!baseName) {
    return undefined;
  }

  const parts = baseName.split('_');

  const width = Number(parts.at(-3));
  const height = Number(parts.at(-2));
  const tileCount = Number(parts.at(-1));

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(tileCount) ||
    width <= 0 ||
    height <= 0 ||
    tileCount <= 0
  ) {
    return undefined;
  }

  return {
    name: spriteUrl,
    ia: [],
    frequency: DEFAULT_FREQUENCY,
    width,
    height,
    rows: Math.ceil(tileCount / DEFAULT_COLUMNS),
    columns: DEFAULT_COLUMNS,
  };
}
